<script setup lang="ts">
import { computed, watch } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { basicTools as editorTools } from "@/appdata/editor-tools";
import { Editor } from '@progress/kendo-vue-editor';
import { stripHtml } from "@/appdata/globals";

const ux = useUxData();
const setupStore = useGuidedSetupStore();

const worldName = computed({
    get: () => setupStore.worldDraft?.name ?? '',
    set: (value: string) => {
        setupStore.updateWorldDraft({ name: value });
    }
});

const isValid = computed(() => {
    return (worldName.value?.trim().length ?? 0) > 0;
});

watch(isValid, (valid) => {
    setupStore.validateStep(0, valid);
}, { immediate: true });

const onDescriptionBlur = (...event: any[] | unknown[]): any => {
    if (!event[0]) return;

    const html = event[0].target.getHTML();
    const result = stripHtml(html);

    if (result.trim().length > 0) {
        setupStore.updateWorldDraft({ summary: html });
    } else {
        setupStore.updateWorldDraft({ summary: '' });
    }
};
</script>

<template>
    <div class="world-name-step">
        <div class="form-group mb-3">
            <label for="guided-world-name" class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.world?.nameLabel"></label>
            <input
                v-model="worldName"
                type="text"
                id="guided-world-name"
                class="form-control"
                maxlength="255"
                :placeholder="ux.labels?.guidedSetup?.world?.namePlaceholder ?? 'Enter a name for your world'"
                autofocus
            />
            <div class="form-text" v-html="ux.labels?.guidedSetup?.world?.nameHelp"></div>
        </div>
        <div class="form-group mb-3">
            <label for="guided-world-description" class="form-label fw-semibold">
              {{ ux.labels?.worlds?.summary ?? 'Description' }}
            </label>
            <!-- Defer Editor init until the modal is shown; a Kendo editor that initializes inside
                 the still-hidden modal renders a blank (childless) toolbar and never recovers. -->
            <Editor
              v-if="setupStore.flowReady"
              :tools="editorTools"
              id="guided-world-description"
              default-edit-mode="div"
              :default-content="setupStore.worldDraft?.summary ?? ''"
              @blur="onDescriptionBlur"
            />
            <div class="form-text" v-html="ux.labels?.guidedSetup?.world?.descriptionHelp"></div>
        </div>
    </div>
</template>
